import { Dropdown, MenuProps, message } from 'antd';
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import type { ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import {
  AddToPlaylist,
  AddToQueueIcon,
  AddToLibrary,
  AddedToLibrary,
  CloseIcon,
  RecordingIcon,
  NextRecordingIcon,
  WorkIcon,
  ArtistIcon,
  VolumeMuteIcon,
  VolumeIcon
} from '../../../components/Icons';
import { Tooltip } from '../../../components/Tooltip';
import type { LibraryRecording } from '../../../interfaces/recording';
import type { Playlist } from '../../../interfaces/playlists';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import {
  fetchMyPlaylists,
  fetchMyRecordings,
  saveRecordingWithCascade,
  removeRecordingWithCascade,
} from '../../../store/slices/yourLibrary';
import { playlistService } from '../../../services/playlists';
import { uiActions } from '../../../store/slices/ui';
import { useMutedEntity } from '../../../hooks/useMutedEntity';
import { useCascadeSelections } from '../../../hooks/useCascadeSelections';
import { CascadeMenu } from '../../../components/Actions/CascadeMenu';
import { DropdownSubPanel } from '../../../components/Actions/DropdownSubPanel';
import { EntityPreferenceMenu } from '../../../components/Actions/EntityPreferenceMenu';
import { useDropdownSubPanel } from '../../../components/Actions/useDropdownSubPanel';
import { usePlayback } from '../../../contexts/PlaybackContext';
import { useComposerIdSet } from '../../../utils/useComposerIdSet';
import { extractSpotifyArtistId, resolveArtistRoute } from '../../../utils/resolveArtistRoute';
import { ensureComposerKnown, getCatalogIdForSpotifyId } from '../../../utils/composerRegistry';
import { isVerboseLoggingEnabled } from '../../../lib/debug/logging';
import { preferencesSelectors, removePreference, upsertPreference } from '../../../store/slices/preferences';

export type RecordingActionsHandle = {
  openRecordingPreferencePanel: () => void;
};

interface RecordingActionsWrapperProps {
  recording: LibraryRecording;
  trigger?: ('click' | 'contextMenu')[];
  children: React.ReactNode;
  hideQueueAction?: boolean;
  hidePlaylistAction?: boolean;
  trackIds?: string[] | null;
  hideGoToRecording?: boolean;
  hideMuteAction?: boolean;
  hideWorkMuteAction?: boolean;
  artists?: Array<{ id: string; name?: string | null }>;
  duplicateGoToArtistButton?: boolean;
  queueRemovalAction?: {
    label?: string;
    onRemove: () => void;
    disabled?: boolean;
    pending?: boolean;
  };
  queueMoveNextAction?: {
    onMoveNext: () => void;
    disabled?: boolean;
    pending?: boolean;
  };
  preferenceWeight?: number;
  onPreferenceChange?: (next: number) => void;
  onPreferenceSave?: (next?: number) => Promise<void> | void;
  preferenceDisabled?: boolean;
  workPreferenceWeight?: number;
  onWorkPreferenceChange?: (next: number) => void;
  onWorkPreferenceSave?: (next?: number) => Promise<void> | void;
  workPreferenceDisabled?: boolean;
  composerPreferenceWeight?: number;
  onComposerPreferenceChange?: (next: number) => void;
  onComposerPreferenceSave?: (next?: number) => Promise<void> | void;
  composerPreferenceDisabled?: boolean;
  hideTooltip?: boolean;
}

const DEFAULT_DROPDOWN_TRIGGER: Array<'click' | 'contextMenu'> = ['click'];

const formatPreferenceLabel = (value: number) => {
  const formatted = value.toFixed(2).replace(/\.?0+$/, '');
  return `${formatted}x`;
};

const clampPreferenceWeight = (value: number): number => {
  if (!Number.isFinite(value)) return 1;
  return value;
};

const RecordingActionsWrapperInner = forwardRef<RecordingActionsHandle, RecordingActionsWrapperProps>(
  (
    {
      recording,
      trigger = DEFAULT_DROPDOWN_TRIGGER,
      children,
      hideQueueAction = false,
      hidePlaylistAction = false,
      trackIds = null,
      hideGoToRecording = false,
      hideMuteAction = false,
      hideWorkMuteAction = false,
      artists = [],
      duplicateGoToArtistButton = false,
      queueRemovalAction,
      queueMoveNextAction,
      preferenceWeight = 1,
      onPreferenceChange,
      onPreferenceSave,
      preferenceDisabled = false,
      workPreferenceWeight = 1,
      onWorkPreferenceChange,
      onWorkPreferenceSave,
      workPreferenceDisabled = false,
      composerPreferenceWeight = 1,
      onComposerPreferenceChange,
      onComposerPreferenceSave,
      composerPreferenceDisabled = false,
      hideTooltip = false,
    },
    ref
  ) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const user = useAppSelector((state) => state.auth.user?.id);
    const { queueSession, queueStatus, performQueueAction } = usePlayback();
    const composerIds = useComposerIdSet();
    const savedRecordings = useAppSelector((state) => state.yourLibrary.myRecordings);
    const myPlaylists = useAppSelector((state) => state.yourLibrary.myPlaylists);
    const {
      isMuted: recordingMuted,
      pending: recordingMutePending,
      toggle: toggleRecordingMute
    } = useMutedEntity('recording', recording.id, {
      label: recording.name ?? recording.workName ?? recording.albumName ?? 'recording',
      initialSource: 'recording-menu'
    });
    const {
      isMuted: workMuted,
      pending: workMutePending,
      toggle: toggleWorkMute
    } = useMutedEntity('work', recording.workId, {
      label: recording.workName ?? 'work',
      initialSource: 'recording-menu'
    });

    const [menuOpen, setMenuOpen] = useState(false);
    const [openKeys, setOpenKeys] = useState<string[]>([]);
    const prefPanel = useDropdownSubPanel();
    const workPrefPanel = useDropdownSubPanel();
    const composerPrefPanel = useDropdownSubPanel();
    const artistPrefPanel = useDropdownSubPanel();
    const artistPrefPanelAlt = useDropdownSubPanel();
    const artistPrefPanelHoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const artistPrefPanelAltHoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const artistPrefPanelAltKeepOpenRef = useRef(false);
    const [isApplyingCascade, setIsApplyingCascade] = useState(false);
    const [isQueueAppendPending, setIsQueueAppendPending] = useState(false);
    const [optimisticSaved, setOptimisticSaved] = useState<boolean | null>(null);
    const [preferenceDraft, setPreferenceDraft] = useState(preferenceWeight);
    const [workPreferenceDraft, setWorkPreferenceDraft] = useState(workPreferenceWeight);
    const [composerPreferenceDraft, setComposerPreferenceDraft] = useState(composerPreferenceWeight);
    const [artistPreferenceDraft, setArtistPreferenceDraft] = useState<number>(1);
    const [artistPreferenceSaving, setArtistPreferenceSaving] = useState(false);
    const [activeArtist, setActiveArtist] = useState<{ id: string; name?: string } | null>(null);
    const [artistPreferenceDraftAlt, setArtistPreferenceDraftAlt] = useState<number>(1);
    const [artistPreferenceSavingAlt, setArtistPreferenceSavingAlt] = useState(false);
    const [activeArtistAlt, setActiveArtistAlt] = useState<{ id: string; name?: string } | null>(null);
    const [hoveredArtistId, setHoveredArtistId] = useState<string | null>(null);
    const verboseLoggingEnabled = useMemo(() => isVerboseLoggingEnabled(), []);
    const isDevelopmentEnv =
      typeof process !== 'undefined' ? process.env.NODE_ENV !== 'production' : true;
    const shouldLogArtists = isDevelopmentEnv && verboseLoggingEnabled;

    const isSaved = useMemo(() => {
      if (optimisticSaved !== null) {
        return optimisticSaved;
      }
      return savedRecordings.some(
        (entry) => entry.id === recording.id || (entry as any).recordingId === recording.id
      );
    }, [optimisticSaved, recording.id, savedRecordings]);

    useEffect(() => {
      const inLibrary = savedRecordings.some(
        (entry) => entry.id === recording.id || (entry as any).recordingId === recording.id
      );
      setOptimisticSaved((prev) => {
        // Avoid thrashing state if the derived value has not changed.
        return prev === inLibrary ? prev : inLibrary;
      });
    }, [recording.id, savedRecordings]);

    const ensureUser = useCallback(
      (button?: boolean) => {
        if (!user) {
          dispatch(button ? uiActions.openLoginButton() : uiActions.openLoginTooltip());
          return false;
        }
        return true;
      },
      [dispatch, user]
    );

    const normalizedTrackIds = useMemo(() => {
      if (!Array.isArray(trackIds)) {
        return null;
      }
      const normalized = Array.from(
        new Set(
          trackIds
            .map((candidate) => (typeof candidate === 'string' ? candidate.trim() : ''))
            .filter((candidate) => candidate.length > 0)
        )
      );
      return normalized.length ? normalized : null;
    }, [trackIds]);

    const normalizedArtists = useMemo<Array<{ id: string; name?: string }>>(() => {
      if (!Array.isArray(artists)) {
        return [];
      }

      const seen = new Set<string>();
      const result: Array<{ id: string; name?: string }> = [];

      for (const artist of artists) {
        const id = typeof artist?.id === 'string' ? artist.id.trim() : '';
        if (!id || seen.has(id)) {
          continue;
        }
        seen.add(id);
        const name =
          typeof artist?.name === 'string' && artist.name.trim().length > 0
            ? artist.name.trim()
            : undefined;
        result.push(name ? { id, name } : { id });
      }

      return result;
    }, [artists]);
    const visibleArtists = useMemo(
      () =>
        normalizedArtists.filter(
          (artist) => Boolean(artist.name && artist.name.trim().length > 0)
        ),
      [normalizedArtists]
    );

    useEffect(() => {
      if (!shouldLogArtists) return;
      const prefix = '[RecordingActions]';
      if (!normalizedArtists.length) {
        console.debug(prefix, 'No artists for recording', recording.id);
      } else {
        console.debug(prefix, 'Artists for recording', recording.id, normalizedArtists);
      }
    }, [normalizedArtists, recording.id, shouldLogArtists]);

    const activeArtistId = activeArtist?.id ?? null;
    const activeArtistAltId = activeArtistAlt?.id ?? null;
    const isArtistDetail = Boolean(activeArtist);
    const artistPanelStyle = useMemo(() => {
      if (isArtistDetail) {
        return {
          width: 222,
          maxWidth: 222,
          minWidth: 222,
          minHeight: 200,
          maxHeight: 260
        };
      }
      return {
        width: 260,
        maxHeight: 340
      };
    }, [isArtistDetail]);

  const closeAllPreferencePanels = useCallback(() => {
    prefPanel.close();
    workPrefPanel.close();
    composerPrefPanel.close();
    artistPrefPanel.close();
      artistPrefPanelAlt.close();
      setActiveArtist(null);
      setActiveArtistAlt(null);
      setOpenKeys((prev) => prev.filter((key) => key !== 'artist-preference-alt'));
    }, [
      artistPrefPanel,
      artistPrefPanelAlt,
      composerPrefPanel,
      prefPanel,
      setActiveArtist,
      setActiveArtistAlt,
      setOpenKeys,
      workPrefPanel
    ]);

    const togglePreferencePanel = useCallback(
      (panel: 'recording' | 'work' | 'composer') => {
        const isOpen =
          (panel === 'recording' && prefPanel.isOpen) ||
          (panel === 'work' && workPrefPanel.isOpen) ||
          (panel === 'composer' && composerPrefPanel.isOpen);

        if (isOpen) {
          closeAllPreferencePanels();
          setMenuOpen(true);
          return;
        }

        closeAllPreferencePanels();
        setMenuOpen(true);
        if (panel === 'recording') {
          prefPanel.open();
        } else if (panel === 'work') {
          workPrefPanel.open();
        } else if (panel === 'composer') {
          composerPrefPanel.open();
        }
      },
      [closeAllPreferencePanels, composerPrefPanel, prefPanel, workPrefPanel]
    );

    const existingArtistPreference = useAppSelector((state) =>
      activeArtistId ? preferencesSelectors.selectPreference(state, 'artist', activeArtistId) : undefined
    );
    const existingArtistPreferenceAlt = useAppSelector((state) =>
      activeArtistAltId ? preferencesSelectors.selectPreference(state, 'artist', activeArtistAltId) : undefined
    );
    const artistPreferenceMap = useAppSelector((state) => {
      const artistPrefs = state.preferences.byType.artist ?? {};
      return visibleArtists.reduce<Record<string, number | undefined>>((acc, artist) => {
        acc[artist.id] = artistPrefs[artist.id]?.weight;
        return acc;
      }, {});
    });
    const {
      isMuted: artistMuted,
      pending: artistMutePending,
      toggle: toggleArtistMute
    } = useMutedEntity('artist', activeArtistId, {
      label: activeArtist?.name ?? 'artist',
      initialSource: 'artist-menu'
    });
    const {
      isMuted: artistMutedAlt,
      pending: artistMutePendingAlt,
      toggle: toggleArtistMuteAlt
    } = useMutedEntity('artist', activeArtistAltId, {
      label: activeArtistAlt?.name ?? 'artist',
      initialSource: 'artist-menu'
    });

    useEffect(() => {
      if (!activeArtistId) {
        setArtistPreferenceDraft(1);
        return;
      }
      setArtistPreferenceDraft(clampPreferenceWeight(existingArtistPreference?.weight ?? 1));
    }, [activeArtistId, existingArtistPreference?.weight]);

    useEffect(() => {
      if (!activeArtistAltId) {
        setArtistPreferenceDraftAlt(1);
        return;
      }
      setArtistPreferenceDraftAlt(clampPreferenceWeight(existingArtistPreferenceAlt?.weight ?? 1));
    }, [activeArtistAltId, existingArtistPreferenceAlt?.weight]);

    const addTracksToPlaylist = useCallback(
      async (playlist: Playlist) => {
        if (!ensureUser(true)) return;

        if (!normalizedTrackIds?.length) {
          void message.error('No tracks available to add for this recording.');
          return;
        }

        const uris = normalizedTrackIds.map((id) =>
          id.startsWith('spotify:track:') ? id : `spotify:track:${id}`
        );

        const batches: string[][] = [];
        for (let i = 0; i < uris.length; i += 100) {
          batches.push(uris.slice(i, i + 100));
        }

        try {
          for (const batch of batches) {
            await playlistService.addPlaylistItems(playlist.id, batch, playlist.snapshot_id);
          }
          dispatch(fetchMyPlaylists());
          void message.success(
            `Added ${uris.length} track${uris.length === 1 ? '' : 's'} to ${playlist.name}`
          );
          setMenuOpen(false);
        } catch (error) {
          console.error('Failed to add recording tracks to playlist', error);
          void message.error('Unable to add to playlist right now.');
        }
      },
      [dispatch, ensureUser, normalizedTrackIds]
    );

    const handleGoToArtist = useCallback(
      async (artistId: string) => {
        const trimmed = artistId.trim();
        if (!trimmed) return;
        const fallbackDestination = resolveArtistRoute({ id: trimmed }, composerIds);
        const spotifyArtistId = extractSpotifyArtistId(trimmed);
        let destination = fallbackDestination;

        if (spotifyArtistId) {
          const isComposer = await ensureComposerKnown(spotifyArtistId);
          if (isComposer) {
            const catalogId = getCatalogIdForSpotifyId(spotifyArtistId) ?? spotifyArtistId;
            destination = `/composer/${catalogId}`;
          }
        }

        navigate(destination);
      },
      [composerIds, navigate]
    );

    const handleQueueAppend = useCallback(async () => {
      if (isQueueAppendPending) {
        return;
      }
      if (!ensureUser(true)) {
        return;
      }
      if (!queueSession || queueStatus !== 'ready') {
        void message.info('Start playback to build a queue before adding recordings.');
        return;
      }

      setIsQueueAppendPending(true);
      try {
        await performQueueAction('append-recordings', { recordingIds: [recording.id] });
        void message.success('Added to queue');
      } catch (error) {
        console.error('Failed to append recording to queue', error);
        void message.error('Failed to add to queue');
      } finally {
        setIsQueueAppendPending(false);
      }
    }, [ensureUser, isQueueAppendPending, performQueueAction, queueSession, queueStatus, recording.id]);

    const handleSave = useCallback(
      async (options?: { cascades?: { tracks?: boolean }; trackIds?: string[] | null }) => {
        try {
          await dispatch(
            saveRecordingWithCascade({
              recordingId: recording.id,
              id: recording.id,
              notes: recording.notes,
              labels: recording.labels ?? [],
              savedAt: recording.savedAt ?? new Date().toISOString(),
              cascades: options?.cascades,
              trackIds: options?.trackIds ?? undefined,
            })
          ).unwrap();
          setOptimisticSaved(true);
          dispatch(fetchMyRecordings());
          void message.success('Saved to Your Library');
          return true;
        } catch (error) {
          const content =
            (typeof error === 'string' && error) ||
            (error instanceof Error && error.message) ||
            'Failed to save to Your Library.';
          void message.error(content);
          return false;
        }
      },
      [dispatch, recording]
    );

    const handleRemove = useCallback(
      async (options?: { cascades?: { tracks?: boolean }; trackIds?: string[] | null }) => {
        try {
          await dispatch(
            removeRecordingWithCascade({
              recordingId: recording.id,
              cascades: options?.cascades,
              trackIds: options?.trackIds ?? undefined,
            })
          ).unwrap();
          setOptimisticSaved(false);
          dispatch(fetchMyRecordings());
          void message.success('Removed from Your Library');
          return true;
        } catch (error) {
          const content =
            (typeof error === 'string' && error) ||
            (error instanceof Error && error.message) ||
            'Failed to remove from Your Library.';
          void message.error(content);
          return false;
        }
      },
      [dispatch, recording.id]
    );

    const mode = isSaved ? 'remove' : 'save';
    const {
      scopes: cascadeScopes,
      selections: cascadeSelections,
      setSelection: setCascadeSelection,
      resetSelections: resetCascadeSelections,
    } = useCascadeSelections({
      entityType: 'recording',
      mode,
    });

    const cascadeLabels = useMemo(() => {
      if (mode === 'save') {
        return {
          recording: 'Save recording',
          tracks: 'Save tracks',
        } as const;
      }
      return {
        recording: 'Remove recording',
        tracks: 'Remove tracks',
      } as const;
    }, [mode]);

    const handleCascadeToggle = useCallback(
      (scope: (typeof cascadeScopes)[number], next: boolean) => {
        setCascadeSelection(scope, next);
      },
      [setCascadeSelection]
    );

    const handleCascadeApply = useCallback(async () => {
      if (!ensureUser(true)) return;
      setIsApplyingCascade(true);

      try {
        const cascades = {
          tracks: cascadeSelections.tracks ?? true,
        };

        const actionSucceeded =
          mode === 'save'
            ? await handleSave({ cascades, trackIds: normalizedTrackIds })
            : await handleRemove({ cascades, trackIds: normalizedTrackIds });

        if (actionSucceeded) {
          resetCascadeSelections();
        }
      } finally {
        // Close submenu the same way clicking the parent menu item again would
        flushSync(() => {
          setOpenKeys((prev) => prev.filter((key) => key !== 'library-cascade'));
        });
        setIsApplyingCascade(false);
      }
    }, [
      cascadeSelections.tracks,
      ensureUser,
      handleRemove,
      handleSave,
      mode,
      normalizedTrackIds,
      resetCascadeSelections,
    ]);

    useEffect(() => {
      if (!menuOpen) {
        setPreferenceDraft(preferenceWeight);
        prefPanel.close();
        setWorkPreferenceDraft(workPreferenceWeight);
        workPrefPanel.close();
        setComposerPreferenceDraft(composerPreferenceWeight);
        composerPrefPanel.close();
        setArtistPreferenceDraft(1);
        setActiveArtist(null);
        setHoveredArtistId(null);
        artistPrefPanel.close();
        setArtistPreferenceDraftAlt(1);
        setActiveArtistAlt(null);
        artistPrefPanelAlt.close();
        if (openKeys.length) {
          setOpenKeys([]);
        }
      }
    }, [
      menuOpen,
      openKeys.length,
      preferenceWeight,
      prefPanel,
      workPreferenceWeight,
      workPrefPanel,
      composerPreferenceWeight,
      composerPrefPanel,
      setHoveredArtistId,
      artistPrefPanel,
      artistPrefPanelAlt
    ]);

  // Cleanup hover timeouts on unmount
  useEffect(() => {
    const hoverTimeout = artistPrefPanelHoverTimeoutRef.current;
    const hoverAltTimeout = artistPrefPanelAltHoverTimeoutRef.current;
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
      if (hoverAltTimeout) {
        clearTimeout(hoverAltTimeout);
      }
    };
  }, []);

    const handlePreferenceApply = useCallback(async () => {
      if (!onPreferenceChange || !onPreferenceSave) return;
      const nextValue = preferenceDraft ?? preferenceWeight;
      onPreferenceChange(nextValue);
      prefPanel.close();
      await onPreferenceSave(nextValue);
    }, [onPreferenceChange, onPreferenceSave, preferenceDraft, preferenceWeight, prefPanel]);

    const handlePreferenceCancel = useCallback(() => {
      setPreferenceDraft(preferenceWeight);
      prefPanel.close();
    }, [preferenceWeight, prefPanel]);

  useEffect(() => {
    setComposerPreferenceDraft(composerPreferenceWeight);
  }, [composerPreferenceWeight, recording.composerId]);

    const handleWorkPreferenceApply = useCallback(async () => {
      if (!onWorkPreferenceChange || !onWorkPreferenceSave) return;
      const nextValue = workPreferenceDraft ?? workPreferenceWeight;
      onWorkPreferenceChange(nextValue);
      workPrefPanel.close();
      await onWorkPreferenceSave(nextValue);
    }, [onWorkPreferenceChange, onWorkPreferenceSave, workPreferenceDraft, workPreferenceWeight, workPrefPanel]);

    const handleWorkPreferenceCancel = useCallback(() => {
      setWorkPreferenceDraft(workPreferenceWeight);
      workPrefPanel.close();
    }, [workPreferenceWeight, workPrefPanel]);

    const handleComposerPreferenceApply = useCallback(async () => {
      if (!onComposerPreferenceChange || !onComposerPreferenceSave || !recording.composerId) return;
      const nextValue = composerPreferenceDraft ?? composerPreferenceWeight;
      onComposerPreferenceChange(nextValue);
      composerPrefPanel.close();
      await onComposerPreferenceSave(nextValue);
    }, [
      onComposerPreferenceChange,
      onComposerPreferenceSave,
      composerPreferenceDraft,
      composerPreferenceWeight,
      composerPrefPanel,
      recording.composerId
    ]);

    const handleComposerPreferenceCancel = useCallback(() => {
      setComposerPreferenceDraft(composerPreferenceWeight);
      composerPrefPanel.close();
    }, [composerPreferenceWeight, composerPrefPanel]);

    const handleArtistPreferenceApply = useCallback(async () => {
      if (!activeArtistId) return false;
      if (!ensureUser(true)) return false;
      const normalizedInput =
        typeof artistPreferenceDraft === 'number' && Number.isFinite(artistPreferenceDraft)
          ? artistPreferenceDraft
          : 1;
      const clamped = clampPreferenceWeight(normalizedInput);
      setArtistPreferenceDraft(clamped);
      setArtistPreferenceSaving(true);
      try {
        if (Math.abs(clamped - 1) < 0.0001) {
          await dispatch(
            removePreference({
              entityType: 'artist',
              entityId: activeArtistId
            })
          ).unwrap();
          setArtistPreferenceDraft(1);
          message.success('Artist preference reset to neutral.');
          return true;
        }

        const saved = await dispatch(
          upsertPreference({
            entityType: 'artist',
            entityId: activeArtistId,
            weight: clamped
          })
        ).unwrap();
        setArtistPreferenceDraft(saved?.weight ?? clamped);
        message.success(
          `Artist preference saved${activeArtist?.name ? ` for ${activeArtist.name}` : ''}.`
        );
        return true;
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.error('[Queue] failed to save artist preference', { activeArtistId, error });
        }
        message.error('Unable to save artist preference right now.');
        return false;
      } finally {
        setArtistPreferenceSaving(false);
      }
    }, [
      activeArtist?.name,
      activeArtistId,
      artistPreferenceDraft,
      dispatch,
      ensureUser
    ]);

    const handleArtistPreferenceCancel = useCallback(() => {
      if (existingArtistPreference?.weight != null) {
        setArtistPreferenceDraft(clampPreferenceWeight(existingArtistPreference.weight));
      } else {
        setArtistPreferenceDraft(1);
      }
    }, [existingArtistPreference?.weight]);

    const handleArtistPreferenceApplyAlt = useCallback(async () => {
      if (!activeArtistAltId) return false;
      if (!ensureUser(true)) return false;
      const normalizedInput =
        typeof artistPreferenceDraftAlt === 'number' && Number.isFinite(artistPreferenceDraftAlt)
          ? artistPreferenceDraftAlt
          : 1;
      const clamped = clampPreferenceWeight(normalizedInput);
      setArtistPreferenceDraftAlt(clamped);
      setArtistPreferenceSavingAlt(true);
      try {
        if (Math.abs(clamped - 1) < 0.0001) {
          await dispatch(
            removePreference({
              entityType: 'artist',
              entityId: activeArtistAltId
            })
          ).unwrap();
          setArtistPreferenceDraftAlt(1);
          message.success('Artist preference reset to neutral.');
          return true;
        }

        const saved = await dispatch(
          upsertPreference({
            entityType: 'artist',
            entityId: activeArtistAltId,
            weight: clamped
          })
        ).unwrap();
        setArtistPreferenceDraftAlt(saved?.weight ?? clamped);
        message.success(
          `Artist preference saved${activeArtistAlt?.name ? ` for ${activeArtistAlt.name}` : ''}.`
        );
        return true;
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.error('[Queue] failed to save artist preference', { activeArtistAltId, error });
        }
        message.error('Unable to save artist preference right now.');
        return false;
      } finally {
        setArtistPreferenceSavingAlt(false);
      }
    }, [
      activeArtistAlt?.name,
      activeArtistAltId,
      artistPreferenceDraftAlt,
      dispatch,
      ensureUser
    ]);

    const handleArtistPreferenceCancelAlt = useCallback(() => {
      if (existingArtistPreferenceAlt?.weight != null) {
        setArtistPreferenceDraftAlt(clampPreferenceWeight(existingArtistPreferenceAlt.weight));
      } else {
        setArtistPreferenceDraftAlt(1);
      }
    }, [existingArtistPreferenceAlt?.weight]);

    const playlistItems = useMemo(() => {
      if (!myPlaylists.length) return [];
      return myPlaylists.map((playlist) => ({
        key: playlist.id,
        label: playlist.name,
        onClick: () => {
          void addTracksToPlaylist(playlist);
        },
      }));
    }, [addTracksToPlaylist, myPlaylists]);

    const hasPlaylistItems = playlistItems.length > 0;

    const triggerRef = useRef<HTMLSpanElement | null>(null);
    const dropdownContentRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      if (!menuOpen) return;

      const closeMenu = () => {
        // Close submenus the same way clicking the parent menu item again would
        flushSync(() => {
          setOpenKeys([]);
        });
        setMenuOpen(false);
        prefPanel.close();
        workPrefPanel.close();
        composerPrefPanel.close();
        artistPrefPanel.close();
        artistPrefPanelAlt.close();
        setActiveArtist(null);
        setActiveArtistAlt(null);
        resetCascadeSelections();
        setIsApplyingCascade(false);
      };

      const handlePointerLikeEvent = (event: Event) => {
        const target = event.target as HTMLElement | null;
        if (!target) return;
        if (triggerRef.current?.contains(target)) return;
        if (dropdownContentRef.current?.contains(target)) return;
        if (target.closest('.recording-actions-dropdown')) return;
        if (target.closest('.cascade-menu__popup')) return;
        // Check if click is inside any subpanel (preference panel, cascade menu, etc.)
        // Also check for Ant Design's native submenu popups
        if (
          target.closest('.dropdown-subpanel-wrapper') !== null ||
          target.closest('.entity-preference-menu') !== null ||
          target.closest('.cascade-menu') !== null ||
          target.closest('.ant-dropdown-menu-submenu-popup') !== null ||
          target.closest('.ant-dropdown-menu-sub') !== null
        ) {
          return;
        }
        closeMenu();
      };

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          closeMenu();
        }
      };

      const listenerOptions: AddEventListenerOptions = { capture: true };
      const pointerEvents = typeof window !== 'undefined' && 'PointerEvent' in window
        ? ['pointerdown']
        : ['mousedown', 'touchstart'];

      const eventTargets: Array<[string, (event: Event) => void]> = pointerEvents.map((name) => [
        name,
        handlePointerLikeEvent,
      ]);
      eventTargets.push(['click', handlePointerLikeEvent]);

      eventTargets.forEach(([name, handler]) =>
        document.addEventListener(name, handler, listenerOptions)
      );
      document.addEventListener('keydown', handleKeyDown, listenerOptions);

      return () => {
        eventTargets.forEach(([name, handler]) =>
          document.removeEventListener(name, handler, listenerOptions)
        );
        document.removeEventListener('keydown', handleKeyDown, listenerOptions);
      };
    }, [menuOpen, prefPanel, workPrefPanel, composerPrefPanel, artistPrefPanel, artistPrefPanelAlt, resetCascadeSelections]);

    const renderDropdown = useCallback(
      (originNode: ReactNode) => {
        if (!originNode) {
          return originNode;
        }
        return (
          <div ref={dropdownContentRef} className='recording-actions-dropdown__content'>
            {originNode}
          </div>
        );
      },
      []
    );

    const menuItems = useMemo<MenuProps['items']>(() => {
      const items: MenuProps['items'] = [];
      const hasQueueRemovalAction = Boolean(queueRemovalAction);
      const hasQueueMoveNextAction = Boolean(queueMoveNextAction);

      if (hasQueueRemovalAction && queueRemovalAction) {
        items.push({
          key: 'queue-remove',
          label: queueRemovalAction.pending
            ? 'Removing…'
            : queueRemovalAction.label ?? 'Remove from queue',
          icon: <CloseIcon />,
          danger: true,
          disabled: queueRemovalAction.disabled || queueRemovalAction.pending,
          onClick: () => {
            if (queueRemovalAction.disabled || queueRemovalAction.pending) {
              return;
            }
            queueRemovalAction.onRemove();
          }
        });
      }

      if (hasQueueMoveNextAction && queueMoveNextAction) {
        items.push({
          key: 'queue-play-next',
          label: queueMoveNextAction.pending ? 'Moving…' : 'Play next',
          icon: <NextRecordingIcon />,
          disabled: queueMoveNextAction.disabled || queueMoveNextAction.pending,
          onClick: () => {
            if (queueMoveNextAction.disabled || queueMoveNextAction.pending) {
              return;
            }
            queueMoveNextAction.onMoveNext();
          }
        });
      }

      items.push({
        key: 'library-cascade',
        label: isSaved ? 'Remove from Your Library' : 'Add to Your Library',
        icon: isSaved ? (
          <AddedToLibrary style={{ width: 16, height: 16 }} />
        ) : (
          <AddToLibrary style={{ width: 16, height: 16 }} />
        ),
        className: 'cascade-menu__trigger-item',
        popupClassName: 'cascade-menu__popup',
        children: [
          {
            key: 'library-cascade-options',
            label: (
              <CascadeMenu
                entityType='recording'
                mode={mode}
                scopes={cascadeScopes}
                selections={cascadeSelections}
                labels={cascadeLabels}
                onToggle={handleCascadeToggle}
                applyLabel='Apply'
                onApply={handleCascadeApply}
                onCancel={() => {
                  resetCascadeSelections();
                  // Close submenu the same way clicking the parent menu item again would
                  flushSync(() => {
                    setOpenKeys((prev) => prev.filter((key) => key !== 'library-cascade'));
                  });
                }}
                applyDisabled={isApplyingCascade}
              />
            ),
            style: { padding: 0, height: 'auto' },
          },
        ],
      });

      if (visibleArtists.length > 0) {
        items.push({ type: 'divider' });
      }

      const goToArtistItem =
        visibleArtists.length > 0
          ? {
              key: 'go-to-artist',
              label: 'Go to artist',
              icon: <ArtistIcon />,
              children: visibleArtists.map((artist) => ({
                key: `go-to-artist-${artist.id}`,
                label: artist.name ?? 'Artist',
                onClick: () => {
                  // Close submenu the same way clicking "go to artist" again would
                  // Use flushSync to ensure openKeys update happens before closing dropdown
                  flushSync(() => {
                    setOpenKeys((prev) => prev.filter((key) => key !== 'go-to-artist'));
                  });
                  setMenuOpen(false);
                  void handleGoToArtist(artist.id);
                }
              }))
            }
          : null;

      // Go-to order: recording, work, artist, composer
      if (!hideGoToRecording) {
        items.push({
          key: 'go-to-recording',
          label: 'Go to recording',
          icon: <RecordingIcon />,
          onClick: () => {
            navigate(`/recording/${encodeURIComponent(recording.id)}`);
          },
        });
      }

      items.push({
        key: 'go-to-work',
        label: 'Go to work',
        icon: <WorkIcon />,
        disabled: !recording.workId,
        onClick: () => {
          if (!recording.workId) return;
          navigate(`/work/${encodeURIComponent(recording.workId)}`);
        }
      });

      if (goToArtistItem) {
        items.push(goToArtistItem);
      }

      items.push({
        key: 'go-to-composer',
        label: 'Go to composer',
        icon: <ArtistIcon />,
        disabled: !recording.composerId,
        onClick: () => {
          if (!recording.composerId) return;
          navigate(`/composer/${encodeURIComponent(recording.composerId)}`);
        }
      });

      if (
        !hidePlaylistAction ||
        (!hideQueueAction && !hasQueueRemovalAction) ||
        !hideMuteAction ||
        (!hideWorkMuteAction && recording.workId) ||
        (onPreferenceChange && onPreferenceSave) ||
        (onComposerPreferenceChange && onComposerPreferenceSave && recording.composerId) ||
        (duplicateGoToArtistButton && visibleArtists.length > 0)
      ) {
        items.push({ type: 'divider' });
      }

      if (onPreferenceChange && onPreferenceSave) {
        const recordingLabelWeight = preferenceWeight;
        items.push({
          key: 'recording-preference',
          label: (
            <div
              ref={(el) => {
                prefPanel.triggerRef.current = el;
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                togglePreferencePanel('recording');
              }}
              style={{ cursor: 'pointer' }}
            >
              Adjust recording freq ({formatPreferenceLabel(recordingLabelWeight)})
            </div>
          ),
          onClick: () => {
            togglePreferencePanel('recording');
          },
          icon: <div style={{ width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><VolumeIcon /></div>,
          children: [{ key: 'recording-preference-dummy', label: '', style: { display: 'none', height: 0, padding: 0, margin: 0 } }], // Dummy children to trigger Ant Design's submenu arrow
          popupClassName: 'cascade-menu__popup',
          popupStyle: { display: 'none' }, // Hide the dummy submenu
        });
      }

      if (!hideWorkMuteAction && recording.workId && onWorkPreferenceChange && onWorkPreferenceSave) {
        const workLabelWeight = workPreferenceWeight;
        items.push({
          key: 'work-preference',
          label: (
            <div
              ref={(el) => {
                workPrefPanel.triggerRef.current = el;
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                togglePreferencePanel('work');
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                paddingRight: '4px',
                justifyContent: 'space-between',
                cursor: 'pointer'
              }}
            >
              Adjust work freq ({formatPreferenceLabel(workLabelWeight)})
            </div>
          ),
          onClick: () => {
            togglePreferencePanel('work');
          },
          icon: <div style={{ width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><VolumeIcon /></div>,
          children: [{ key: 'work-preference-dummy', label: '', style: { display: 'none', height: 0, padding: 0, margin: 0 } }], // Dummy children to trigger Ant Design's submenu arrow
          popupClassName: 'cascade-menu__popup',
          popupStyle: { display: 'none' }, // Hide the dummy submenu
        });
      }

      if (duplicateGoToArtistButton && visibleArtists.length > 0) {
        items.push({
          key: 'artist-preference-alt',
          label: (
            <div
              ref={(el) => {
                artistPrefPanelAlt.triggerRef.current = el;
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                if (artistPrefPanelAlt.isOpen || openKeys.includes('artist-preference-alt')) {
                  // Close both the panel and the artist list submenu
                  flushSync(() => {
                    setOpenKeys((prev) => prev.filter((key) => key !== 'artist-preference-alt'));
                  });
                  artistPrefPanelAlt.close();
                  setActiveArtistAlt(null);
                  return;
                }

                closeAllPreferencePanels();
                setActiveArtistAlt(null);
                setMenuOpen(true);
                setOpenKeys((prev) =>
                  prev.includes('artist-preference-alt')
                    ? prev
                    : [...prev, 'artist-preference-alt']
                );
              }}
              style={{
                cursor: 'pointer',
                display: 'inline-block',
                margin: '-4px -12px',
                padding: '4px 12px',
                minWidth: '100%'
              }}
            >
              Adjust artist freq
            </div>
          ),
          icon: (
            <div
              style={{
                width: 16,
                height: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <VolumeIcon />
            </div>
          ),
          children: visibleArtists.map((artist) => ({
            key: `artist-preference-alt-${artist.id}`,
            label: `${artist.name ?? 'Artist'} (${formatPreferenceLabel(
              clampPreferenceWeight(artistPreferenceMap[artist.id] ?? 1)
            )})`,
            onClick: (info) => {
              info?.domEvent?.preventDefault?.();
              info?.domEvent?.stopPropagation?.();
              // @ts-expect-error stopImmediatePropagation exists on MouseEvent
              info?.domEvent?.stopImmediatePropagation?.();
              closeAllPreferencePanels();
              artistPrefPanelAltKeepOpenRef.current = true;
              setActiveArtistAlt({ id: artist.id, name: artist.name ?? 'Artist' });
              artistPrefPanelAlt.open();
              setMenuOpen(true);
            }
          }))
        });
      }

      if (onComposerPreferenceChange && onComposerPreferenceSave && recording.composerId) {
        const composerLabelWeight = composerPreferenceWeight;
        items.push({
          key: 'composer-preference',
          label: (
            <div
              ref={(el) => {
                composerPrefPanel.triggerRef.current = el;
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                togglePreferencePanel('composer');
              }}
              style={{ cursor: 'pointer' }}
            >
              Adjust composer freq ({formatPreferenceLabel(composerLabelWeight)})
            </div>
          ),
          onClick: () => {
            togglePreferencePanel('composer');
          },
          icon: (
            <div
              style={{
                width: 16,
                height: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <VolumeIcon />
            </div>
          ),
          children: [{ key: 'composer-preference-dummy', label: '', style: { display: 'none', height: 0, padding: 0, margin: 0 } }],
          popupClassName: 'cascade-menu__popup',
          popupStyle: { display: 'none' },
        });
      }

      if (!hidePlaylistAction) {
        items.push({
          key: 'playlist',
          label: 'Add to playlist',
          icon: <AddToPlaylist />,
          disabled: !user || !hasPlaylistItems,
          children: hasPlaylistItems ? playlistItems : undefined,
          onClick: () => {
            if (!ensureUser()) return;
          }
        });
      }

      if (!hideQueueAction && !hasQueueRemovalAction) {
        items.push({
          key: 'queue',
          label: isQueueAppendPending ? 'Adding…' : 'Add to queue',
          icon: <AddToQueueIcon />,
          disabled: isQueueAppendPending,
          onClick: () => {
            void handleQueueAppend();
          }
        });
      }

      if (!hideMuteAction) {
        items.push({
          key: 'mute-recording',
          label: recordingMuted ? 'Unmute recording' : 'Mute recording',
          icon: <VolumeMuteIcon />,
          disabled: recordingMutePending,
          onClick: () => {
            if (!ensureUser()) return;
            void toggleRecordingMute({ source: 'recording-menu' });
          }
        });
      }

      if (hasQueueRemovalAction || hasQueueMoveNextAction || !hideQueueAction || !hidePlaylistAction) {
        items.push({ type: 'divider' });
      }

      return items;
  }, [
    cascadeLabels,
    cascadeScopes,
    cascadeSelections,
    ensureUser,
    handleCascadeApply,
    handleCascadeToggle,
    hasPlaylistItems,
    hidePlaylistAction,
    hideQueueAction,
    hideGoToRecording,
    hideMuteAction,
    hideWorkMuteAction,
    isApplyingCascade,
    isSaved,
    visibleArtists,
    duplicateGoToArtistButton,
    activeArtistAlt,
    artistPreferenceDraftAlt,
    existingArtistPreferenceAlt?.weight,
    artistPreferenceMap,
    mode,
    navigate,
    playlistItems,
    queueRemovalAction,
    queueMoveNextAction,
    recording.id,
    recording.workId,
    recording.composerId,
    recordingMuted,
    recordingMutePending,
    handleQueueAppend,
    isQueueAppendPending,
    handleGoToArtist,
    resetCascadeSelections,
    toggleRecordingMute,
    user,
    onComposerPreferenceChange,
    onComposerPreferenceSave,
    composerPreferenceWeight,
    composerPrefPanel,
    onPreferenceChange,
    onPreferenceSave,
    preferenceWeight,
    prefPanel,
    togglePreferencePanel,
    onWorkPreferenceChange,
    onWorkPreferenceSave,
    workPreferenceWeight,
    workPrefPanel,
    artistPrefPanelAlt,
    closeAllPreferencePanels,
    openKeys
  ]);

    useImperativeHandle(
      ref,
      () => ({
        openRecordingPreferencePanel: () => {
          togglePreferencePanel('recording');
        }
      }),
      [togglePreferencePanel]
    );

    const dropdown = (
      <Dropdown
        trigger={trigger}
        menu={{
          items: menuItems,
          triggerSubMenuAction: 'click',
          openKeys: openKeys.filter(
            (key) =>
              key !== 'recording-preference' &&
              key !== 'work-preference' &&
              key !== 'composer-preference' &&
              key !== 'go-to-artist-duplicate'
          ), // Allow real submenus, prevent dummy ones (freq + artist panel)
          onOpenChange: (keys) => {
            // Filter out dummy submenu keys (recording/work/composer prefs + artist panel)
            const realKeys = keys.filter(
              (key) =>
                key !== 'recording-preference' &&
                key !== 'work-preference' &&
                key !== 'composer-preference' &&
                key !== 'go-to-artist-duplicate'
            );
            // If cascade menu is opening, close all preference panels
            if (realKeys.includes('library-cascade') && !openKeys.includes('library-cascade')) {
              closeAllPreferencePanels();
            }
            setOpenKeys(realKeys);
          }
        }}
        overlayClassName='recording-actions-dropdown'
        placement='bottomRight'
        destroyPopupOnHide
        getPopupContainer={() => document.body}
        open={
          menuOpen ||
          prefPanel.isOpen ||
          workPrefPanel.isOpen ||
          composerPrefPanel.isOpen ||
          artistPrefPanel.isOpen ||
          artistPrefPanelAlt.isOpen ||
          Boolean(activeArtistAlt)
        }
        dropdownRender={renderDropdown}
        onOpenChange={(open) => {
          if (
            !open &&
            (prefPanel.isOpen ||
              workPrefPanel.isOpen ||
              composerPrefPanel.isOpen ||
              artistPrefPanel.isOpen ||
              artistPrefPanelAlt.isOpen ||
              activeArtistAlt ||
              artistPrefPanelAltKeepOpenRef.current)
          ) {
            artistPrefPanelAltKeepOpenRef.current = false;
            return;
          }
          setMenuOpen(open);
          if (open) {
            return;
          }
          prefPanel.close();
          workPrefPanel.close();
          composerPrefPanel.close();
          artistPrefPanel.close();
          artistPrefPanelAlt.close();
          resetCascadeSelections();
          setIsApplyingCascade(false);
          setOpenKeys([]);
        }}
      >
        <span ref={triggerRef}>{children}</span>
      </Dropdown>
    );

    return (
      <>
        {hideTooltip ? (
          dropdown
        ) : (
          <Tooltip title='More options'>{dropdown}</Tooltip>
        )}
        {artistPrefPanel.isOpen && duplicateGoToArtistButton && visibleArtists.length > 0 && (
          <DropdownSubPanel
            isOpen={artistPrefPanel.isOpen}
            zIndex={9999}
            onClose={() => {
              artistPrefPanel.close();
              setActiveArtist(null);
            }}
            triggerElement={artistPrefPanel.triggerRef.current as HTMLElement | null}
            parentDropdownOpen={menuOpen}
            onParentDropdownChange={setMenuOpen}
            onMouseEnter={() => {
              if (artistPrefPanelHoverTimeoutRef.current) {
                clearTimeout(artistPrefPanelHoverTimeoutRef.current);
                artistPrefPanelHoverTimeoutRef.current = null;
              }
            }}
            onMouseLeave={() => {
              artistPrefPanelHoverTimeoutRef.current = setTimeout(() => {
                artistPrefPanel.close();
                artistPrefPanelHoverTimeoutRef.current = null;
              }, 150);
            }}
          >
            <div
              style={{
                ...artistPanelStyle,
                padding: isArtistDetail ? '6px' : '6px',
                boxSizing: 'border-box',
                color: '#fff',
                display: 'flex',
                flexDirection: 'column',
                gap: isArtistDetail ? 4 : 4,
                overflow: 'hidden'
              }}
            >
              {activeArtist ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button
                      type='button'
                      onClick={() => {
                        setActiveArtist(null);
                        setArtistPreferenceDraft(1);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        background: 'transparent',
                        border: 'none',
                        color: '#fff',
                        padding: '6px 8px',
                        textAlign: 'left',
                        cursor: 'pointer'
                      }}
                    >
                      <span aria-hidden>←</span>
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {activeArtist.name ?? 'Artist'}
                      </span>
                    </button>
                  </div>
                  <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                    <div style={{ width: 210, minHeight: 171 }}>
                      <EntityPreferenceMenu
                        entityType='artist'
                        value={artistPreferenceDraft}
                        disabled={artistPreferenceSaving}
                        isMuted={artistMuted}
                        mutePending={artistMutePending}
                        onToggleMute={() => {
                          void toggleArtistMute({ source: 'artist-menu' });
                        }}
                        onChange={(next) => setArtistPreferenceDraft(clampPreferenceWeight(next))}
                        onApply={async () => {
                          const success = await handleArtistPreferenceApply();
                          if (success) {
                            artistPrefPanel.close();
                          }
                        }}
                        onCancel={handleArtistPreferenceCancel}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0,
                    overflowY: 'auto',
                    padding: '6px 0'
                  }}
                >
                  {visibleArtists.map((artist) => (
                    <button
                      key={`go-to-artist-duplicate-${artist.id}`}
                      type='button'
                      onClick={() => {
                        setActiveArtist({ id: artist.id, name: artist.name ?? 'Artist' });
                        setHoveredArtistId(null);
                      }}
                      onMouseEnter={() => {
                        setHoveredArtistId(artist.id);
                      }}
                      onMouseLeave={() => {
                        setHoveredArtistId((current) => (current === artist.id ? null : current));
                      }}
                      style={{
                        display: 'block',
                        width: '100%',
                        height: 40,
                        padding: '0 16px',
                        background: hoveredArtistId === artist.id ? '#3a3a3a' : 'transparent',
                        border: 'none',
                        color: '#fff',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: 16,
                        lineHeight: 1.3,
                        borderRadius: 0
                      }}
                    >
                      {`${artist.name ?? 'Artist'} (${formatPreferenceLabel(
                        clampPreferenceWeight(artistPreferenceMap[artist.id] ?? 1)
                      )})`}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </DropdownSubPanel>
        )}
        {artistPrefPanelAlt.isOpen && activeArtistAlt && (
          <DropdownSubPanel
            isOpen={artistPrefPanelAlt.isOpen}
            zIndex={9999}
            onClose={() => {
              artistPrefPanelAlt.close();
              setActiveArtistAlt(null);
            }}
            triggerElement={artistPrefPanelAlt.triggerRef.current as HTMLElement | null}
            parentDropdownOpen={menuOpen}
            onParentDropdownChange={setMenuOpen}
          >
            <div
              style={{
                width: 222,
                maxWidth: 222,
                minWidth: 222,
                padding: '6px',
                boxSizing: 'border-box',
                color: '#fff',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                overflow: 'hidden'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  type='button'
                  onClick={() => {
                    setActiveArtistAlt(null);
                    setArtistPreferenceDraftAlt(1);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    background: 'transparent',
                    border: 'none',
                    color: '#fff',
                    padding: '6px 8px',
                    textAlign: 'left',
                    cursor: 'pointer'
                  }}
                >
                  <span aria-hidden>←</span>
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {activeArtistAlt.name ?? 'Artist'}
                  </span>
                </button>
              </div>
              <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                <div style={{ width: 210, minHeight: 171 }}>
                  <EntityPreferenceMenu
                    entityType='artist'
                    value={artistPreferenceDraftAlt}
                    disabled={artistPreferenceSavingAlt}
                    isMuted={artistMutedAlt}
                    mutePending={artistMutePendingAlt}
                    onToggleMute={() => {
                      void toggleArtistMuteAlt({ source: 'artist-menu' });
                    }}
                    onChange={(next) => setArtistPreferenceDraftAlt(clampPreferenceWeight(next))}
                    onApply={async () => {
                      const success = await handleArtistPreferenceApplyAlt();
                      if (success) {
                        artistPrefPanelAlt.close();
                      }
                    }}
                    onCancel={handleArtistPreferenceCancelAlt}
                  />
                </div>
              </div>
            </div>
          </DropdownSubPanel>
        )}
        {/* Preference Panel */}
        {prefPanel.isOpen && onPreferenceChange && onPreferenceSave && (
          <DropdownSubPanel
            isOpen={prefPanel.isOpen}
            zIndex={9999}
          onClose={prefPanel.close}
          triggerElement={prefPanel.triggerRef.current as HTMLElement | null}
          parentDropdownOpen={menuOpen}
          onParentDropdownChange={setMenuOpen}
        >
            <EntityPreferenceMenu
              entityType='recording'
              value={preferenceDraft}
              disabled={preferenceDisabled}
              isMuted={recordingMuted}
              mutePending={recordingMutePending}
              onToggleMute={() => {
                void toggleRecordingMute({ source: 'recording-menu' });
              }}
              onChange={setPreferenceDraft}
              onApply={handlePreferenceApply}
              onCancel={handlePreferenceCancel}
            />
          </DropdownSubPanel>
        )}
        {/* Composer Preference Panel */}
        {composerPrefPanel.isOpen &&
          onComposerPreferenceChange &&
          onComposerPreferenceSave &&
          recording.composerId && (
            <DropdownSubPanel
              isOpen={composerPrefPanel.isOpen}
              zIndex={9999}
              onClose={composerPrefPanel.close}
              triggerElement={composerPrefPanel.triggerRef.current as HTMLElement | null}
              parentDropdownOpen={menuOpen}
              onParentDropdownChange={setMenuOpen}
            >
              <EntityPreferenceMenu
                entityType='composer'
                value={composerPreferenceDraft}
                disabled={composerPreferenceDisabled}
                isMuted={false}
                mutePending={false}
                onToggleMute={() => {}}
                onChange={setComposerPreferenceDraft}
                onApply={handleComposerPreferenceApply}
                onCancel={handleComposerPreferenceCancel}
              />
            </DropdownSubPanel>
          )}
        {/* Work Preference Panel */}
        {workPrefPanel.isOpen && onWorkPreferenceChange && onWorkPreferenceSave && recording.workId && (
          <DropdownSubPanel
            isOpen={workPrefPanel.isOpen}
            zIndex={9999}
          onClose={workPrefPanel.close}
          triggerElement={workPrefPanel.triggerRef.current as HTMLElement | null}
          parentDropdownOpen={menuOpen}
          onParentDropdownChange={setMenuOpen}
        >
            <EntityPreferenceMenu
              entityType='work'
              value={workPreferenceDraft}
              disabled={workPreferenceDisabled}
              isMuted={workMuted}
              mutePending={workMutePending}
              onToggleMute={() => {
                void toggleWorkMute({ source: 'recording-menu' });
              }}
              onChange={setWorkPreferenceDraft}
              onApply={handleWorkPreferenceApply}
              onCancel={handleWorkPreferenceCancel}
            />
          </DropdownSubPanel>
        )}
      </>
    );
  }
);

RecordingActionsWrapperInner.displayName = 'RecordingActionsWrapper';

export const RecordingActionsWrapper = memo(RecordingActionsWrapperInner);
